import { useCallback, useMemo, useState } from 'react'
import { IconArrowUp } from '@tabler/icons-react'
import classNames from 'classnames'
import { Button } from '~/components/Button'
import { Image } from '~/components/Image'
import ProjectStepsAccordion from '~/components/ProjectStepsAccordion/ProjectStepsAccordion'
import { ProjectStep } from '~/components/ProjectStepsAccordion/ProjectStepsAccordion.types'
import RichText from '~/components/RichText/RichText'
import { SectionContainer } from '~/components/SectionContainer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import styles from './ProjectSteps.module.css'
import { ProjectStepsProps } from './ProjectSteps.types'

export const ProjectSteps = ({
  steps,
  conclusion,
  width,
}: ProjectStepsProps) => {
  const [completedSteps, setCompletedSteps] = useState<Array<string>>([])

  const hasConclusion = useMemo(() => {
    return (
      (!Array.isArray(conclusion.futureImprovements) &&
        !!conclusion.futureImprovements) ||
      (Array.isArray(conclusion.futureImprovements) &&
        conclusion.futureImprovements.length > 0)
    )
  }, [conclusion.futureImprovements])

  const setStepCompleteStatus = useCallback(
    (stepId: string) => {
      if (completedSteps.includes(stepId)) {
        setCompletedSteps(
          completedSteps.filter((id) => {
            return id !== stepId
          })
        )
      } else {
        setCompletedSteps([...completedSteps, stepId])
      }
    },
    [completedSteps, setCompletedSteps]
  )

  const scrollToPosition = (x: number) => {
    // Check if the browser supports the 'scrollBehavior' property (modern browsers do).
    if ('scrollBehavior' in document.documentElement.style) {
      // Use smooth scrolling if supported.
      window.scrollTo({
        top: x,
        behavior: 'smooth',
      })
    }
  }

  const isProjectComplete = useMemo(() => {
    return (
      steps.length === completedSteps.length &&
      steps.every(({ stepId }) => {
        return completedSteps.includes(stepId)
      })
    )
  }, [completedSteps, steps])

  return (
    <SectionContainer
      wrapperClassName={classNames(styles.root)}
      innerClassName={classNames('flex flex-col gap-6 pb-8 mx-auto')}
      width={width}>
      <div
        className={classNames(styles.tableOfContents, 'rounded-md p-6 mb-14')}>
        <Typography
          tag={TypographyTag.p}
          variant={TypographyVariant.BodyXLargeBold}>
          Table of Contents
        </Typography>
        <ul className={classNames('list-disc', 'ml-4', 'list-inside')}>
          {steps.length > 0 &&
            steps.map((step: ProjectStep, index: number) => {
              return (
                <li key={step.stepId}>
                  <a
                    className={classNames('underline')}
                    href={`#${step.title}-${index}`}>
                    <Typography
                      tag={TypographyTag.span}
                      variant={TypographyVariant.BodyRegular}>
                      {step.title}
                    </Typography>
                  </a>
                </li>
              )
            })}
        </ul>
      </div>

      <ProjectStepsAccordion
        completedSteps={completedSteps}
        steps={steps}
        setStepCompleteStatus={setStepCompleteStatus}
      />

      {isProjectComplete && (
        <div
          className={classNames(
            styles.projectComplete,
            'my-14 flex items-center justify-center flex-col'
          )}>
          <Typography
            className={classNames('pb-2')}
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyXLargeBold}>
            That’s it! Well done on completing this project.
          </Typography>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyRegular}>
            Share your finished work on social media and hashtag <b>#Jaycar</b>
          </Typography>
          {conclusion.projectCompleteImage && (
            <Image
              className={classNames('mx-auto', 'rounded-md', 'mt-6')}
              {...conclusion?.projectCompleteImage}
              alt={conclusion.projectCompleteImage?.altText}
              loading='eager'
            />
          )}

          <div className={classNames('pt-14')}>
            <Button
              onClick={() => {
                return scrollToPosition(0)
              }}
              icon={<IconArrowUp />}
              size='md'
              shape='square'
              status='secondary'
              style={{
                '--btn-icon-size': '12px',
              }}>
              Back to the top
            </Button>
          </div>
        </div>
      )}

      {hasConclusion && (
        <div className={classNames(styles.futureImprovements)}>
          <Typography
            className={classNames('pb-2')}
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyXLargeBold}>
            Future Improvements
          </Typography>
          <RichText content={conclusion.futureImprovements} />
        </div>
      )}
    </SectionContainer>
  )
}
